import { useState } from 'react';
import { Check, Copy, CreditCard, Link2 } from 'lucide-react';
import { PayPanel } from '@generated/components/PayPanel';
import { money } from '@generated/components/pricing';

const PORTAL = 'https://ad-portal-gbrook8344s-team-company.v.monday.app';
const METHODS = ['Credit Card', 'Zelle', 'Venmo', 'PayPal'] as const;
type Method = (typeof METHODS)[number];
const label = 'text-xs font-semibold uppercase tracking-wider';
const field = 'mt-1 block h-11 w-full border border-input bg-background px-3 text-sm';
const chip = (on: boolean) => `min-h-11 border-2 px-4 text-sm font-semibold ${on ? 'border-foreground bg-foreground text-background' : 'border-border'}`;
const refOf = (s: string) => s.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'payment';

/** Customer pay page (opened from a link staff send). Staff also get a link maker at the bottom. */
export function PayPage({ amount, forWhat, staff }: { amount?: string; forWhat?: string; staff: boolean }) {
  const preset = Number(amount);
  const [amt, setAmt] = useState(preset > 0 ? String(preset) : '');
  const [what, setWhat] = useState(forWhat ?? '');
  const [method, setMethod] = useState<Method>('Credit Card');
  const n = Math.round(Number(amt) * 100) / 100;
  const ok = n >= 1;

  return (
    <section className="mx-auto max-w-2xl">
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Pay the Star-Revue</div>
      <h2 className="mt-1 font-[family-name:var(--font-heading)] text-4xl font-semibold leading-tight">Make a payment</h2>
      <p className="mt-2 text-sm text-muted-foreground">Thank you for advertising with the <strong className="text-foreground">Red Hook Star-Revue</strong> and <strong className="text-foreground">The Village Star-Revue</strong>. Choose how you'd like to pay below.</p>

      <div className="mt-6 border-2 border-foreground bg-card p-5">
        {preset > 0 ? (
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div><div className={label}>For</div><div className="text-lg font-semibold">{forWhat || 'Advertising'}</div></div>
            <div className="text-right"><div className={label}>Amount due</div><div className="font-[family-name:var(--font-heading)] text-3xl font-bold tabular-nums">{money(preset)}</div></div>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <div><label htmlFor="pp-what" className={label}>What is this for?</label><input id="pp-what" placeholder="Business name, ad, invoice #" value={what} onChange={(e) => setWhat(e.target.value)} className={field} /></div>
            <div><label htmlFor="pp-amt" className={label}>Amount ($)</label><input id="pp-amt" inputMode="decimal" value={amt} onChange={(e) => setAmt(e.target.value.replace(/[^\d.]/g, ''))} className={field} /></div>
          </div>
        )}
        <div className="mt-5"><div className={label}>Pay with</div>
          <div className="mt-2 flex flex-wrap gap-2">{METHODS.map((m) => <button key={m} type="button" onClick={() => setMethod(m)} className={chip(method === m)}>{m === 'Credit Card' ? 'Credit card' : m}</button>)}</div></div>
      </div>

      {ok ? <PayPanel method={method} orderId={refOf(what || "payment")} dueNow={n} total={n} monthly={false} kind="Pay link" who={(preset > 0 ? forWhat : what) || undefined} />
        : <p className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground"><CreditCard className="h-4 w-4" />Enter the amount to see payment steps.</p>}
      <p className="mt-6 text-sm text-muted-foreground">Questions about your bill? Call George at <a href="tel:9176529128" className="font-semibold text-foreground underline">917-652-9128</a>.</p>

      {staff && <LinkMaker />}
    </section>
  );
}

/** Staff: build a payment link with the amount and description filled in. */
function LinkMaker() {
  const [what, setWhat] = useState('');
  const [amt, setAmt] = useState('');
  const [copied, setCopied] = useState(false);
  const n = Number(amt);
  const link = `${PORTAL}/?go=pay${n > 0 ? `&amt=${n}` : ''}${what.trim() ? `&for=${encodeURIComponent(what.trim())}` : ''}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); }
    catch { const ta = document.createElement('textarea'); ta.value = link; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove(); }
    setCopied(true); setTimeout(() => setCopied(false), 2500);
  };
  return (
    <div className="mt-10 border-2 border-dashed border-foreground p-5">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary"><Link2 className="h-4 w-4" />Staff only · Make a payment link</div>
      <p className="mt-1 text-sm text-muted-foreground">Fill in what it's for and the amount, then copy the link and email or text it to the customer. Leave both blank for a general "pay us" link.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_140px]">
        <div><label htmlFor="lm-what" className={label}>For</label><input id="lm-what" placeholder="e.g. Joe's Pizza · Quarter page, Nov 2026" value={what} onChange={(e) => setWhat(e.target.value)} className={field} /></div>
        <div><label htmlFor="lm-amt" className={label}>Amount ($)</label><input id="lm-amt" inputMode="decimal" value={amt} onChange={(e) => setAmt(e.target.value.replace(/[^\d.]/g, ''))} className={field} /></div>
      </div>
      <div className="mt-3 break-all border border-border bg-muted p-3 text-xs">{link}</div>
      <button type="button" onClick={() => void copy()} className="mt-3 inline-flex min-h-11 items-center gap-2 bg-foreground px-4 text-sm font-semibold text-background">
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{copied ? 'Copied' : 'Copy link'}</button>
    </div>
  );
}
